import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    ArrowRight,
    Sparkle,
    Crown,
    Lightning,
    Warning,
    Clock,
    X,
} from "@phosphor-icons/react";

// One deterministic next-best-action card, powered by /api/me/next-action.
// Priority handled server-side; the client stays dumb — just renders whatever
// tone/label/href arrives.
//
// Tones map to visual treatments:
//   - warning:   red-accent, urgency copy (expiring pro, boost about to lapse)
//   - highlight: splash, first-time-user CTAs
//   - info:      neutral card
//   - neutral:   quiet card, no strong CTA
const TONE_STYLES = {
    warning: {
        wrapper: "bg-ink text-white border-transparent",
        chip: "bg-red-500 text-white",
        cta: "btn-gradient text-ink",
        icon: Warning,
    },
    highlight: {
        wrapper: "bg-splash text-ink border-transparent",
        chip: "bg-ink text-splash",
        cta: "bg-ink hover:bg-cobalt text-white",
        icon: Sparkle,
    },
    info: {
        wrapper: "bg-white text-ink border-[#E5E5E5]",
        chip: "bg-cobalt/10 text-cobalt",
        cta: "bg-ink hover:bg-cobalt text-white",
        icon: Lightning,
    },
    neutral: {
        wrapper: "bg-white text-ink border-[#E5E5E5]",
        chip: "bg-[#F5F5F5] text-[#525252]",
        cta: "border border-ink hover:bg-ink hover:text-white text-ink",
        icon: Clock,
    },
};

// Icon per kind — small, quick visual hook.
const KIND_ICON = {
    pro_renewal_expiring: Crown,
    pro_expired: Crown,
    boost_expiring: Lightning,
    boost_stuck_application: Lightning,
    cv_rewrite_upsell: Sparkle,
    first_time_rate_cv: Sparkle,
    browse_jobs: ArrowRight,
    track_apps: Clock,
};

const DISMISS_KEY = "nd_next_action_dismissed";

export default function NextActionCard() {
    const [action, setAction] = useState(null);
    const [dismissedKey, setDismissedKey] = useState(null);

    useEffect(() => {
        // Load per-session dismissal so we don't nag on refresh.
        try {
            setDismissedKey(sessionStorage.getItem(DISMISS_KEY));
        } catch {
            /* ignore */
        }
        api.get("/me/next-action")
            .then((r) => setAction(r.data))
            .catch(() => setAction(null));
    }, []);

    if (!action) return null;
    // The `track_apps` fallback and any user-dismissed action get suppressed —
    // the whole point is that the card only shows when there's something to do.
    if (action.kind === "track_apps") return null;
    if (dismissedKey === action.kind) return null;

    const tone = TONE_STYLES[action.tone] || TONE_STYLES.info;
    const KindIcon = KIND_ICON[action.kind] || tone.icon;

    const variant = action?.experiment?.variant;

    const dismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY, action.kind); } catch { /* ignore */ }
        setDismissedKey(action.kind);
    };

    const trackClick = () => {
        api.post("/me/next-action/track", {
            kind: "click", variant, action_kind: action.kind,
        }).catch(() => {/* silent — non-critical */});
    };

    return (
        <div
            data-testid={`next-action-${action.kind}`}
            className={`relative border p-5 sm:p-6 mb-6 ${tone.wrapper}`}
        >
            {/* Dismiss */}
            <button
                type="button"
                onClick={dismiss}
                data-testid="next-action-dismiss"
                aria-label="Dismiss"
                className={`absolute top-3 right-3 opacity-40 hover:opacity-100 transition-opacity ${
                    action.tone === "warning" ? "text-white" :
                    action.tone === "highlight" ? "text-ink" : "text-[#525252]"
                }`}
            >
                <X size={14} weight="bold" />
            </button>

            <div className="flex items-start gap-4">
                <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${tone.chip}`}>
                    <KindIcon size={18} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className={`text-[10px] font-black uppercase tracking-[0.2em] mb-1 ${
                        action.tone === "warning" ? "text-splash" :
                        action.tone === "highlight" ? "text-ink/60" : "text-[#A3A3A3]"
                    }`}>
                        Continue where you left off
                    </p>
                    <p className="font-heading text-lg sm:text-xl font-black tracking-tight leading-tight">
                        {action.title}
                    </p>
                    <p className={`text-sm mt-1 leading-relaxed ${
                        action.tone === "warning" ? "text-white/70" :
                        action.tone === "highlight" ? "text-ink/70" : "text-[#525252]"
                    }`}>
                        {action.subtitle}
                    </p>
                </div>
            </div>

            <div className="mt-4 sm:mt-5 flex items-center justify-between gap-3 flex-wrap">
                <Link
                    to={action.cta_href}
                    onClick={trackClick}
                    data-testid={`next-action-cta-${action.kind}`}
                    data-variant={variant}
                    className={`px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 transition-colors ${tone.cta}`}
                >
                    {action.cta_label} <ArrowRight size={12} weight="bold" />
                </Link>
                {action.meta?.code && (
                    <p className={`text-[10px] font-mono ${
                        action.tone === "warning" ? "text-white/60" : "text-[#A3A3A3]"
                    }`}>
                        Code · {action.meta.code}
                    </p>
                )}
            </div>
        </div>
    );
}
